import { enums } from '..';
import * as dm from '../../data-models';
import {
  IPostDocumentArgsWithCaseId,
  IPostDocumentArgsWithPlanId,
} from '../../data-models/web-apis';
import {
  callApiNoProc,
  deleteApi,
  getApi,
  postApi,
  putApi,
} from './same-origin';

// deletes
export const deleteAutoSave = async (formName: string) =>
  deleteApi(enums.ApiPaths.autoSave, { 'x-unum-formname': formName });
export const deleteCaches = async (type: 'employeeTasks') =>
  deleteApi(`${enums.ApiPaths.caches}/${type}`);
export const deleteDraftClaim = async (planId: string) =>
  deleteApi(enums.ApiPathsEX.draftClaim, { planId });

// gets

export const getAbsenceNotifications = async () =>
  getApi<dm.IAbsenceNotifications>(enums.ApiPaths.absenceNotifications);

export const getllPlanPath = async (planId: string) => {
  // callApiNoProc so a failed LeaveLogic lookup doesn't trigger logout or count
  // toward the error limit; callers fall back to the default /app/start intake
  // when this returns null.
  try {
    const response = await callApiNoProc(enums.ApiPaths.llPlanPath, 'get', {
      'x-unum-param-plan-id': planId,
    });
    return response.ok ? await response.json() : null;
  } catch {
    return null;
  }
};
export const getPtoChoice = async (
  custNo?: string,
  ntn?: string,
  planId?: string,
) =>
  getApi<string>(enums.ApiPaths.ptoChoice, {
    'x-unum-param-cust-no': custNo ?? '',
    'x-unum-param-ntn': ntn ?? '',
    'x-unum-param-plan-id': planId ?? '',
  });

export const putPtoChoice = async (
  ntn: string | undefined,
  ptoUpdate: string,
) =>
  putApi(enums.ApiPaths.ptoChoice, {
    'x-unum-param-ntn': ntn,
    'x-unum-param-pto-update': ptoUpdate,
  });

export const getAbsencePeriodDecisions = async (absenceId: string) =>
  getApi<dm.AbsencePeriodDecision>(enums.ApiPaths.absencePeriodDecisions, {
    'x-unum-param-absence-id': absenceId,
  });

export const getAccommodationCase = async (accommodationCaseId: string) =>
  getApi<dm.AccommodationCase>(enums.ApiPaths.accommodationCases, {
    'x-unum-param-accommodation-id': accommodationCaseId,
  });

export const getActivities = async (
  activityType: string | string[],
  processedStatus?: boolean,
) => {
  const headers = {
    'x-unum-param-activity-types': Array.isArray(activityType)
      ? activityType.join(',')
      : activityType,
    ...(processedStatus === undefined
      ? undefined
      : {
          'x-unum-param-processed-status': processedStatus.toString(),
        }),
  };
  return getApi<dm.IActivities>(enums.ApiPaths.activities, headers);
};

export const getAutoSave = async (formName: string) =>
  getApi<dm.IAutoSaveResponse>(enums.ApiPaths.autoSave, {
    'x-unum-formname': formName,
  });
export const getAvailableCallbackTimes = async (
  companyCode: string,
  queryDate: string,
) =>
  getApi<{ availableTimesArray: dm.CallCenterHoursDropDownItem[] }>(
    `${enums.ApiPaths.callbacksAvailable}/?date=${queryDate}`,
    { 'x-unum-param-company-code': companyCode },
  );
export const getCalendarData = async (
  startDate?: string,
  endDate?: string,
  planId?: string,
) => {
  const headers: any = {};
  const queryParams: any = {
    startDate,
    endDate,
  };
  if (planId) {
    headers.planId = planId;
  }
  return getApi<any>('/ab/calendar', headers, queryParams);
};

export const getCases = async () => getApi<dm.Cases>(enums.ApiPathsEX.cases);

export const getCaseHistory = async (ntn: string) => {
  const headers: any = {
    'x-unum-param-ntn': ntn,
  };

  const response = await callApiNoProc(
    enums.ApiPathsEX.caseHistory,
    'get',
    headers,
  );

  if (!response.ok) {
    const error = new Error(
      `Failed to fetch case history: ${response.status} ${response.statusText}`,
    ) as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  return data as dm.ICaseHistoryEvent[];
};

export const getDocument = async (caseid: string, documentid: string) =>
  getApi<any>(enums.ApiPathsEX.document, {
    caseid,
    documentid,
  });
export const getDocuments = async (caseid: string) =>
  getApi<dm.Documents>(enums.ApiPathsEX.documents, {
    caseid,
  });
export const getDraftClaims = async () =>
  getApi<dm.DraftClaims>(enums.ApiPathsEX.draftClaims);
export const getEmployerPreferences = async () =>
  getApi<{} | undefined>(enums.ApiPathsEX.employerPreferences);
export const getLeaveAvailibility = async (planId: string, calcDate: string) =>
  getApi<dm.LeaveAvailibility>(enums.ApiPaths.LeaveAvailibility, {
    'x-unum-param-plan-id': planId,
    'x-unum-param-calc-date': calcDate,
  });
export const getMergeUploadedFilesBucketUrl = async (
  caseId: string,
  fileName: string,
  contentType: string,
  timeStamp: string,
) =>
  getApi<any>(enums.ApiPaths.MergeUploadedFilesBucketUrl, {
    'x-unum-param-caseid': caseId,
    'x-unum-param-filename': fileName,
    'x-unum-param-contenttype': contentType,
    'x-unum-param-timestamp': timeStamp,
  });
export const getPaymentPreferences = async () =>
  getApi<any>(enums.ApiPathsEX.getPaymentPreferences); // no model
export const getPerson = async () => getApi<dm.Person>(enums.ApiPathsEX.person);
export const getPersonData = async () =>
  getApi<dm.PersonData>(enums.ApiPathsEX.personData);
export const getPreferences = async () =>
  getApi<dm.CommunicationPreferences>(enums.ApiPathsEX.preferences);
export const getRefresh = async () => {
  const response = await callApiNoProc(enums.ApiPathsEX.refresh, 'get');
  if (response.ok) {
    return response.clone().json();
  }
  return {};
};
export const getReminders = async (openOnly = true, smsPrefsEnabled = true) =>
  getApi<{
    finalRemindersArray: dm.ReminderItem[];
  }>(`${enums.ApiPaths.remindersFunction}`, {
    'x-unm-open-reminders-only': String(openOnly),
    'x-unm-sms-prefs-enabled': String(smsPrefsEnabled),
  });

export const getSessionInfo = async () =>
  getApi<{
    username?: string;
    maxIdleExpirationTime?: string;
    maxSessionExpirationTime?: string;
    eid?: string;
    firstName?: string;
    lastName?: string;
  }>(enums.ApiPaths.sessionInfo);
export const getTask = async (taskId: string) =>
  getApi<dm.Task>(`${enums.ApiPathsAB.tasks}/${taskId}`);
export const getTaskFormDownloadUrl = async (taskId: string) =>
  getApi<any>(`${enums.ApiPathsAB.tasks}/${taskId}/documents`) as Promise<
    dm.TaskDocumentsObject[]
  >;
export const getTaskHistory = async (taskId: string) =>
  getApi<any>(
    `${enums.ApiPathsAB.tasks}/${taskId}/history`,
  ) as Promise<dm.TaskHistoryResponse>;
export const getTasks = async (queryParams: {
  caseId?: string;
  notificationCaseId?: string;
  customerNumber?: number;
  taskStatusId?: number;
  assignee?: number;
  tokenId?: string;
  lastKey?: string;
  sortKey?: string;
  sortOrder?: string;
}) => getApi<dm.Tasks>(enums.ApiPathsAB.tasks, {}, queryParams);
export const getVBCoverage = async () =>
  getApi<dm.ICoverageV1Response>(enums.ApiPaths.vbCoverage);
// always resolves so callers can render without VB claims when the API fails
export const getVBClaims = async (): Promise<dm.IVBClaimsResponse> => {
  try {
    const response = await getApi<dm.IVBClaimsResponse>(
      enums.ApiPaths.vbClaims,
    );
    return { claims: response?.claims ?? [] };
  } catch {
    return { claims: [] };
  }
};
export const getCorpLeaveIncrements = async (formName: string) =>
  getApi<dm.ICorpLeaveIncrementsResponse>(enums.ApiPaths.corpLeaveIncrements, {
    'x-unum-param-form-name': formName,
  });
export const getZelleEnrollmentsStatus = async (telephonenumber?: string) => {
  return getApi<any>(
    enums.ApiPathsEX.getZelleEnrollmentsStatus,
    telephonenumber
      ? {
          telephonenumber,
        }
      : undefined,
  );
};

// posts
export const closeReminder = async (reminderId: string) =>
  postApi(`${enums.ApiPaths.closeReminder}/?reminderId=${reminderId}`);
export const createReminder = async (body: dm.ReminderRequest) =>
  postApi(`${enums.ApiPaths.createReminder}`, {}, body);
export const postAutoSave = async (body: dm.IAutoSave) =>
  postApi<any>(
    enums.ApiPaths.autoSave,
    {
      'x-unum-formname': body.formName,
    },
    body,
  );
export const postAbsences = async (caseId: string, body: dm.EpisodicAbsence) =>
  postApi<any>(enums.ApiPathsEX.episodicAbsences, { caseid: caseId }, body);
export const postADA = async (body: dm.AccommodationSubmission) =>
  postApi<any>(enums.ApiPathsEX.ada, undefined, body);
export const postCallback = async (body: { [key: string]: any }) =>
  postApi<any>(enums.ApiPathsEX.callback, undefined, body);
export const postConvertFile = async (file: string) =>
  postApi<{ file: string }>(enums.ApiPaths.ConvertFile, undefined, {
    file,
  });
export const postDocument = async (
  args: IPostDocumentArgsWithCaseId | IPostDocumentArgsWithPlanId,
) => {
  const documentBody: any = {
    filename: args.fileName,
    contenttype: args.contentType,
    action: args.actionType,
    // we should have at least one of these at this point
    ...(args.caseId && { caseid: args.caseId }),
    ...(args.planId && { planid: args.planId }),
    // below are optional properties
    ...(args.taskKey && { taskkey: args.taskKey }),
  };

  return postApi<any>(enums.ApiPathsEX.document, undefined, documentBody);
};
export const postDraftClaim = async (body: dm.DraftClaim) =>
  postApi<any>(enums.ApiPathsEX.draftClaim, undefined, body);
export const postESignature = async (
  caseid: string,
  request: dm.EsignatureRequest,
) => postApi<any>(enums.ApiPathsEX.esignature, { caseid }, request);
export const postExtensionRequest = async (
  caseId: string,
  body: dm.LeaveExtensions,
) =>
  postApi<any>(
    enums.ApiPathsAN.leaveExtensions,
    {
      caseid: caseId,
    },
    body,
  );
export const postLDW = async (body: any, caseid: string) =>
  postApi<any>(enums.ApiPathsEX.ldw, { caseid }, body);
export const postLogout = async () => postApi<any>(enums.ApiPaths.logout);
export const postMergeUploadedFiles = async (
  sortableFiles: any[],
  docType: string,
  vbClaimIds: string[] = [],
) =>
  postApi<any>(
    enums.ApiPaths.MergeUploadedFiles,
    vbClaimIds.length
      ? { 'x-unum-param-vb-claimlist-attach': vbClaimIds.join(',') }
      : undefined,
    {
      fileOrder: sortableFiles,
      documentType: docType,
    },
  );
export const postPaymentPreferences = async (body: any) =>
  callApiNoProc(
    enums.ApiPathsEX.setPaymentPreferences,
    'post',
    undefined,
    body,
  );
export const postPreferences = async (
  preferences: dm.CommunicationPreferences,
) => postApi<any>(enums.ApiPathsEX.preferences, undefined, preferences);
export const postPregnancy = async (body: dm.Pregnancy) =>
  postApi<any>(enums.ApiPathsEX.pregnancy, undefined, body);
export const postQualtrics = async (body: dm.VBSurveyBody, survey: string) =>
  postApi<any>(
    enums.ApiPaths.qualtrics,
    { 'x-unum-param-survey': survey },
    body,
  );
export const postRefresh = async (
  triggerLoginRefresh: boolean,
  triggerFullRefresh: boolean,
) =>
  postApi<any>(enums.ApiPathsEX.refresh, undefined, {
    triggerLoginRefresh,
    triggerFullRefresh,
  });
/**
 * Reloads a case's documents and reports which of the requested docIds now
 * match a document. Uses callApiNoProc because a 500 here means "not yet",
 * and shouldn't count toward the logout-on-repeated-errors limit.
 * @returns The available docIds, or undefined when the refresh didn't succeed
 */
export const postDocumentRefresh = async (
  caseId: string,
  docIds: string[],
): Promise<string[] | undefined> => {
  const response = await callApiNoProc(
    enums.ApiPathsEX.refresh,
    'post',
    { caseid: caseId, 'Content-Type': 'application/json' },
    {
      triggerFullRefresh: false,
      triggerLoginRefresh: false,
      immediateData: [enums.RefreshDetailTypes.documentDetails],
      docIds,
    },
  );
  if (!response.ok) {
    return undefined;
  }
  const data = await response.json();
  return (data?.availableDocIds ?? []).map(String);
};
export const postRefreshToken = async () =>
  postApi<any>(enums.ApiPaths.RefreshToken);
export const postSubmission = async (
  planid: string,
  submissionESignatures: dm.SubmissionESignature,
) => {
  const headers: any = {
    planid,
  };

  // Derive the intake type from the current form URL, e.g.
  // /app/form/personal-medical -> "personal-medical".
  const intakeType = /\/form\/([^/?#]+)/.exec(window.location.pathname)?.[1];

  if (intakeType) {
    headers['x-unum-param-intake-type'] = intakeType;
  }

  return postApi<any>(
    enums.ApiPathsEX.submission,
    headers,
    submissionESignatures,
  );
};
export const postTask = async (taskId: string, body: any) =>
  postApi(`${enums.ApiPathsAB.tasks}/${taskId}`, undefined, body);
export const postVBSubmission = async (vbSubmission: dm.VBSubmission) =>
  postApi<any>(enums.ApiPaths.vbSubmission, undefined, vbSubmission);
export const postWebMessage = async (body: dm.Message, caseid?: string) =>
  postApi<any>(
    enums.ApiPathsEX.webmessage,
    caseid ? { caseid } : undefined,
    body,
  );

// puts
export const putDraftClaim = async () => putApi(enums.ApiPathsEX.draftClaim);
